/**
 * The restaurant-account kitchen PWAs (Kitchen: kds.<domain>, /kds, /pwa/kds;
 * Expo: expo.<domain>, /expo, /pwa/expo). Each renders ONE Restaurant OS screen
 * (apps/dining/screens/KDSScreen or ExpoScreen - the station tickets of
 * /api/dining/kitchen/*, live from the dining event stream) under the PWA's own
 * restaurant-account sign-in (components/pwa/PwaAccountGate). The old
 * order-level boards (/api/kds, /api/expo) are retired.
 * docs/DINING_API_2026-10-28.md §11 / §14 / §15.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { pwaPath } from '@/utils/getBasePath';
import { PwaAccountGate } from '@/components/pwa/PwaAccountGate';
import { getAccount, getAccountToken, signOutAccount } from '@/lib/pwaAccount';
import '@/apps/dining/theme/dining.css';
import { setAuthSource } from '@/apps/dining/lib/api';
import { DiningSession, useMe, usePerms } from '@/apps/dining/lib/session';
import { Btn, Loading, ToastProvider } from '@/apps/dining/components/ui';
import { IconLogout } from '@/apps/dining/components/icons';
import { C, FONT_TIGHT } from '@/apps/dining/theme/tokens';

// the account's chosen location when it is not the token's own (as lib/pwaAccount.accountHeaders)
function accountMerchantId() {
  const account = getAccount();
  if (!account) return null;
  return String(account.restaurant.id) !== String(account.tokenRestaurantId) ? String(account.restaurant.id) : null;
}

const ACCOUNT_AUTH = { getToken: getAccountToken, merchantId: accountMerchantId };

function Note({ title, body, action }) {
  return (
    <div className="c29 c29-dark" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: 460, maxWidth: '100%', background: C.panel, border: `1px solid ${C.line}`, borderRadius: 24, padding: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h1 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 28, fontWeight: 800, color: C.textHi }}>{title}</h1>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: C.textMid }}>{body}</p>
        {action}
      </div>
    </div>
  );
}

function SignOutButton({ me, what }) {
  const signOut = () => {
    if (window.confirm(`Sign this ${what} screen out of the restaurant account?`)) signOutAccount();
  };
  return (
    <button type="button" onClick={signOut} className="c29-press" aria-label={`Signed in as ${me?.name || 'the restaurant account'} - sign out`}
      title="Sign out of this device"
      style={{ width: 40, height: 40, borderRadius: 10, border: `1px solid ${C.line2}`, background: C.panel2, color: C.textMid, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flex: 'none' }}>
      <IconLogout size={19} />
    </button>
  );
}

function AccountScreen({ Screen, what }) {
  const { me, error, loading, reload } = useMe();
  const perms = usePerms(me);
  const ctx = useMemo(() => ({ me, perms, reload }), [me, perms, reload]);
  useEffect(() => {
    if (error && error.status === 401) signOutAccount('Your session ended. Please sign in again.');
  }, [error]);
  if (loading && !me) {
    return <div className="c29 c29-dark" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loading label={`Opening the ${what}…`} /></div>;
  }
  if (error && !me) {
    return error.status === 403
      ? <Note title={`This account cannot open the ${what}`} body={error.message} action={<Btn kind="hero" full onClick={() => signOutAccount()}>Sign in with another account</Btn>} />
      : <Note title="Cannot reach chain29" body={error.message} action={<Btn kind="hero" full onClick={reload}>Try again</Btn>} />;
  }
  if (!perms.can('kds.read')) {
    return <Note title={`No ${what} access`} body="This sign-in cannot read the kitchen tickets. Sign in with the restaurant's owner or manager account." />;
  }
  return (
    <DiningSession.Provider value={ctx}>
      <Screen accountMenu={<SignOutButton me={me} what={what} />} />
    </DiningSession.Provider>
  );
}

// The dining API calls carry the PWA account while this app is open: set
// during render, before any screen's effects; dropped (back to the staff
// sign-in) only once no kitchen app is mounted - a re-mount after a sign-in
// change runs its children's effects before its own, so the drop waits a tick.
let mounted = 0;
function WithAccountAuth({ children }) {
  useState(() => { setAuthSource(ACCOUNT_AUTH); return true; });
  useEffect(() => {
    mounted += 1;
    setAuthSource(ACCOUNT_AUTH);
    return () => {
      mounted -= 1;
      setTimeout(() => { if (mounted === 0) setAuthSource(null); }, 0);
    };
  }, []);
  return children;
}

// The screen shows only the signed-in restaurant account's tickets: no
// account, no screen (the one shared PWA sign-in, components/pwa).
export default function KitchenPwa({ app, appName, icon, accent, Screen, what }) {
  return (
    <PwaAccountGate appName={appName} icon={icon} accent={accent}>
      {(account) => (
        <WithAccountAuth key={`${account.restaurant.id}:${account.signedInAt}`}>
          <ToastProvider>
            <Routes>
              <Route index element={<AccountScreen Screen={Screen} what={what} />} />
              <Route path="*" element={<Navigate to={pwaPath(app)} replace />} />
            </Routes>
          </ToastProvider>
        </WithAccountAuth>
      )}
    </PwaAccountGate>
  );
}
